import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type MorningOlderSuggestion, type MorningSuggestion, type MySuggestions } from '../../lib/api';
import { SHARED, dueDay, wordsOf } from '../todos/todoFormat';
import { LINK, NOT_SAVED, OUTLINE, PRIMARY, ROW } from './MorningRows';

/**
 * MORNING2-AI Task 14 (design 5.1, 5.3, 5.4, 6) - "Suggested for you", below Needs you: up to five suggestions, each with
 * Add to my tasks / Not needed, a "more" line that names what is behind the five, an Older list, and the run's own words when
 * a morning's run was skipped or failed.
 *  - ⛔ An unread list says so ("Couldn't load suggestions.") - an empty group is never read as "nothing to do".
 *  - A switched-off person (`on: false`) gets nothing: no heading, no words.
 *  - Add and Not needed are the server's one-tap-wins: a refusal (409 someone got there first, 502 GoHighLevel) puts its
 *    words on that row and leaves it in place. "Not needed" is this group's word for what cards call Hide.
 */

const NO_CONTACT = 'No GoHighLevel contact on this deal';
const NONE = 'Nothing to suggest this morning.';
/** R62 (unknown is not empty): no run recorded today - not the same as a run that found nothing. */
const NOT_RUN = "Suggestions haven't run yet today.";
const UNREAD = "Couldn't load suggestions.";

type Loaded = Extract<MySuggestions, { on: true }>;
type State = { kind: 'loading' } | { kind: 'off' } | { kind: 'failed' } | { kind: 'ok'; data: Loaded };
type Tap = { busy: 'adding' | 'dismissing' | null; added: boolean; gone: boolean; error: string | null };
const IDLE: Tap = { busy: null, added: false, gone: false, error: null };

/** A DATE, not an instant: read at noon UTC so Pacific never moves it to the day before (as the deadline card does). */
const dueWords = (d: string | null): string | null => (d ? `Due ${dueDay(`${d.slice(0, 10)}T12:00:00.000Z`)}` : null);

/** Where Open goes - the places phase 1's cards already open. A deal -> its page; a call note -> the contact in Conversations. */
function openTo(s: MorningSuggestion): string | null {
  if (s.deal_id) return `/crm/deals/${encodeURIComponent(s.deal_id)}`;
  if (s.source_kind === 'call_note' && s.ghl_contact_id) return `/crm/conversations?${new URLSearchParams({ contact: s.ghl_contact_id })}`;
  return null;
}

function Facts({ s }: { s: MorningSuggestion }) {
  const due = dueWords(s.due_date);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 break-words text-[15px] font-medium text-gray-900">{s.title}</p>
        {s.user_id === null && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{SHARED}</span>}
      </div>
      {due && <p className="mt-0.5 text-[13px] text-gray-500">{due}</p>}
      <p className="mt-0.5 min-w-0 break-words text-[13px] leading-[18px] text-gray-500">{`From: ${s.source_quote}`}</p>
    </>
  );
}

function Row({ s, tap, onAdd, onDismiss }: { s: MorningSuggestion; tap: Tap; onAdd: () => void; onDismiss: () => void }) {
  const to = openTo(s);
  return (
    <li className={ROW}>
      <Facts s={s} />
      {tap.added ? (
        <p className="mt-3 text-sm font-medium text-gray-700">Added - on your task list</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {s.ghl_contact_id ? (
            <button type="button" className={PRIMARY} disabled={tap.busy !== null} onClick={onAdd}>
              {tap.busy === 'adding' ? 'Adding...' : 'Add to my tasks'}
            </button>
          ) : (
            <span className="text-sm text-gray-600">{NO_CONTACT}</span>
          )}
          <button type="button" className={OUTLINE} disabled={tap.busy !== null} onClick={onDismiss}>Not needed</button>
          {to && <Link to={to} className={LINK}>Open</Link>}
        </div>
      )}
      {tap.error && <p role="alert" className="mt-2 break-words text-sm text-red-700">{tap.error}</p>}
    </li>
  );
}

/** What happened to an older one, in words. */
const outcomeWords = (o: MorningOlderSuggestion): string =>
  o.outcome === 'added' ? `Added by ${o.by_name ?? 'someone'}`
    : o.outcome === 'dismissed' ? `Not needed (${o.by_name ?? 'someone'})`
      : 'Left alone';

export default function SuggestedForYou() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [taps, setTaps] = useState<Record<string, Tap>>({});
  const [showMore, setShowMore] = useState(false);
  const [showOlder, setShowOlder] = useState(false);

  useEffect(() => {
    let live = true;
    api.morning.suggestions().then(
      (a) => { if (live) setState(a.on === false ? { kind: 'off' } : { kind: 'ok', data: a as unknown as Loaded }); },
      () => { if (live) setState({ kind: 'failed' }); },
    );
    return () => { live = false; };
  }, []);

  if (state.kind === 'loading' || state.kind === 'off') return null;

  const set = (id: string, over: Partial<Tap>) => setTaps((t) => ({ ...t, [id]: { ...(t[id] ?? IDLE), ...over } }));

  const add = (id: string) => {
    set(id, { busy: 'adding', error: null });
    // ⛔ A 200 `{ on: false }` is the server doing nothing: never "Added" for a task that does not exist (spec 5.4).
    api.morning.addSuggestion(id).then(
      (a) => (a.on === false || !a.added ? set(id, { busy: null, error: NOT_SAVED }) : set(id, { busy: null, added: true })),
      (e: unknown) => set(id, { busy: null, error: wordsOf(e, NOT_SAVED) }),
    );
  };
  const dismiss = (id: string) => {
    set(id, { busy: 'dismissing', error: null });
    api.morning.dismissSuggestion(id).then(
      (a) => (a.on === false || !a.dismissed ? set(id, { busy: null, error: NOT_SAVED }) : set(id, { busy: null, gone: true })),
      (e: unknown) => set(id, { busy: null, error: wordsOf(e, NOT_SAVED) }),
    );
  };

  let body;
  if (state.kind === 'failed') {
    body = <p role="status" className="text-sm text-gray-600">{UNREAD}</p>;
  } else {
    const { view, run } = state.data;
    const shown = view.shown.filter((s) => !taps[s.id]?.gone);
    const rest = (view.rest ?? []).filter((s) => !taps[s.id]?.gone);
    const runWords = run && run.status !== 'ok' && run.words ? run.words : null;
    // With nothing shown: "nothing to suggest" only when today's run finished ok; no run yet says so instead.
    const emptyWords = run === null ? NOT_RUN : run.status === 'ok' ? NONE : null;
    body = (
      <>
        {runWords && <p role="status" className="mb-3 break-words text-sm text-gray-600">{runWords}</p>}
        {shown.length > 0 ? (
          <ul className="space-y-3">
            {shown.map((s) => <Row key={s.id} s={s} tap={taps[s.id] ?? IDLE} onAdd={() => add(s.id)} onDismiss={() => dismiss(s.id)} />)}
          </ul>
        ) : !runWords && emptyWords && <p className="text-sm text-gray-600">{emptyWords}</p>}
        {view.more_line && (
          <>
            <button type="button" aria-expanded={showMore} onClick={() => setShowMore((v) => !v)} className={`${LINK} mt-3 block text-left`}>
              {view.more_line}
            </button>
            {showMore && (
              rest.length > 0 ? (
                <ul aria-label="More suggestions" className="mt-3 space-y-3">
                  {rest.map((s) => <Row key={s.id} s={s} tap={taps[s.id] ?? IDLE} onAdd={() => add(s.id)} onDismiss={() => dismiss(s.id)} />)}
                </ul>
              ) : (
                <ul aria-label="More suggestions" className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                  {view.more_names.map((n, i) => <li key={i} className="break-words">{n}</li>)}
                </ul>
              )
            )}
          </>
        )}
        {view.older.length > 0 && (
          <>
            <button type="button" aria-expanded={showOlder} onClick={() => setShowOlder((v) => !v)} className={`${LINK} mt-3 block`}>
              {`Older (${view.older.length})`}
            </button>
            {showOlder && (
              <ul aria-label="Older suggestions" className="mt-3 space-y-3">
                {view.older.map((o) => (
                  <li key={o.id} className={ROW}>
                    <Facts s={o} />
                    <p className="mt-2 text-sm font-medium text-gray-700">{outcomeWords(o)}</p>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <section aria-label="Suggested for you" className="min-w-0">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Suggested for you</h2>
      {body}
    </section>
  );
}
